import { useState, useMemo } from 'react';
import { useApp } from '@/context/AppContext';
import { Client } from '@/types';
import { formatCurrency, generateId, exportToCSV } from '@/utils/calculations';
import Modal from '@/components/base/Modal';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import StatusBadge from '@/components/base/StatusBadge';
const emptyForm = (): Partial<Client> => ({ name: '', phone: '', email: '', balance: 0, totalTransferred: 0, totalMyCommission: 0, totalProviderCommission: 0, settledAmount: 0 });

export default function ClientsPage() {
  const { isDark, clients, addClient, updateClient, deleteClient, transfers, clientPayments } = useApp();
  const allClients = clients;
  const allTransfers = transfers;

  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<Client | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<Client>>(emptyForm());
  const [search, setSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const filtered = useMemo(() => allClients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) || c.email.toLowerCase().includes(search.toLowerCase())
  ), [allClients, search]);

  const clientTransfers = useMemo(() =>
    selectedClient ? allTransfers.filter(t => t.clientId === selectedClient.id || t.clientName === selectedClient.name) : [],
    [selectedClient, allTransfers]
  );

  // Per-client receivable calculations
  const getClientReceivables = (clientId: string, clientName: string) => {
    const cTransfers = allTransfers.filter(t => (t.clientId === clientId || t.clientName === clientName) && t.status !== 'cancelled');
    const totalNetDue = cTransfers.reduce((s, t) => s + t.netAmountDue, 0);
    const totalReceived = clientPayments.filter(p => p.clientId === clientId || p.clientName === clientName).reduce((s, p) => s + p.amountReceived, 0);
    const outstanding = Math.max(0, totalNetDue - totalReceived);
    return { totalNetDue, totalReceived, outstanding };
  };

  const getTransferReceived = (transferId: string) =>
    clientPayments.filter(p => p.transferId === transferId).reduce((s, p) => s + p.amountReceived, 0);

  const openAdd = () => { setForm(emptyForm()); setEditItem(null); setShowModal(true); };
  const openEdit = (c: Client) => { setForm({ ...c }); setEditItem(c); setShowModal(true); };

  const handleSave = () => {
    if (!form.name) return;
    const client: Client = {
      id: editItem?.id ?? generateId(),
      name: form.name ?? '', phone: form.phone ?? '', email: form.email ?? '',
      totalTransferred: form.totalTransferred ?? 0, totalMyCommission: form.totalMyCommission ?? 0,
      totalProviderCommission: form.totalProviderCommission ?? 0, balance: form.balance ?? 0,
      settledAmount: form.settledAmount ?? 0, createdAt: editItem?.createdAt ?? new Date().toISOString().split('T')[0],
    };
    if (editItem) updateClient(client); else addClient(client);
    setShowModal(false);
  };

  const td = `px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`;
  const th = `text-xs font-semibold uppercase tracking-wide px-4 py-3 text-left whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`;

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div>
          <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Clients</h2>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{filtered.length} clients</p>
        </div>
        <div className="sm:ml-auto flex gap-2">
          <button onClick={() => exportToCSV(filtered.map(c => ({ Name: c.name, Phone: c.phone, Email: c.email, Balance: c.balance, TotalTransferred: c.totalTransferred })), 'clients')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}><i className="ri-download-line" /> Export</button>
          <button onClick={openAdd} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap"><i className="ri-add-line" /> New Client</button>
        </div>
      </div>

      <input placeholder="Search clients..." value={search} onChange={e => setSearch(e.target.value)} className={`w-full max-w-sm px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder:text-gray-500' : 'bg-white border-gray-200 text-gray-700 placeholder:text-gray-400'}`} />

      <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
              <tr>
                <th className={th}>Name</th>
                <th className={th}>Phone</th>
                <th className={th}>Email</th>
                <th className={th}>Total Transferred</th>
                <th className={th}>My Commission</th>
                <th className={th}>Total Net Due</th>
                <th className={th}>Received</th>
                <th className={th}>Outstanding</th>
                <th className={th}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filtered.map(c => {
                const rec = getClientReceivables(c.id, c.name);
                return (
                  <tr key={c.id} className={`transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                    <td className={`${td} font-medium`}>
                      <button onClick={() => setSelectedClient(c)} className="text-emerald-500 hover:underline cursor-pointer">{c.name}</button>
                    </td>
                    <td className={td}>{c.phone}</td>
                    <td className={td}>{c.email}</td>
                    <td className={`${td} font-semibold`}>{formatCurrency(c.totalTransferred)}</td>
                    <td className={td}>{formatCurrency(c.totalMyCommission)}</td>
                    <td className={`${td} font-semibold`}>{formatCurrency(rec.totalNetDue)}</td>
                    <td className={td}><span className="text-emerald-500 font-semibold">{formatCurrency(rec.totalReceived)}</span></td>
                    <td className={td}>
                      {rec.outstanding > 0
                        ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{formatCurrency(rec.outstanding)}</span>
                        : rec.totalNetDue > 0
                          ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Fully Received</span>
                          : <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>—</span>
                      }
                    </td>
                    <td className={td}>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSelectedClient(c)} className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer text-sky-500 hover:bg-sky-50"><i className="ri-eye-line text-sm" /></button>
                        <button onClick={() => openEdit(c)} className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer text-emerald-500 hover:bg-emerald-50"><i className="ri-edit-line text-sm" /></button>
                        <button onClick={() => setDeleteId(c.id)} className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer text-red-400 hover:bg-red-50"><i className="ri-delete-bin-line text-sm" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className={`text-center py-10 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No clients found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Client Ledger Modal */}
      <Modal isOpen={!!selectedClient} onClose={() => setSelectedClient(null)} title={`Ledger: ${selectedClient?.name}`} size="xl">
        {selectedClient && (() => {
          const rec = getClientReceivables(selectedClient.id, selectedClient.name);
          return (
            <div className="space-y-4">
              {/* Receivable Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Total Net Due', value: formatCurrency(rec.totalNetDue), color: isDark ? 'text-white' : 'text-gray-900' },
                  { label: 'Total Received', value: formatCurrency(rec.totalReceived), color: 'text-emerald-500' },
                  { label: 'Outstanding', value: formatCurrency(rec.outstanding), color: rec.outstanding > 0 ? 'text-amber-500' : 'text-emerald-500' },
                  { label: 'My Commission', value: formatCurrency(selectedClient.totalMyCommission), color: 'text-teal-500' },
                ].map(k => (
                  <div key={k.label} className={`rounded-lg p-3 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{k.label}</p>
                    <p className={`text-base font-bold mt-1 ${k.color}`}>{k.value}</p>
                  </div>
                ))}
              </div>

              {/* Outstanding badge */}
              {rec.outstanding > 0 && (
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${isDark ? 'bg-amber-900/30 border border-amber-800' : 'bg-amber-50 border border-amber-200'}`}>
                  <i className="ri-error-warning-line text-amber-500" />
                  <p className={`text-sm font-medium ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                    {formatCurrency(rec.outstanding)} still outstanding from this client
                  </p>
                </div>
              )}
              {rec.outstanding === 0 && rec.totalNetDue > 0 && (
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${isDark ? 'bg-emerald-900/30 border border-emerald-800' : 'bg-emerald-50 border border-emerald-200'}`}>
                  <i className="ri-checkbox-circle-line text-emerald-500" />
                  <p className={`text-sm font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>All amounts fully received from this client</p>
                </div>
              )}

              <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Transaction History</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                      <th className="px-3 py-2 text-left text-xs font-semibold">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold">Txn ID</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold">Amount</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold">Net Due</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold">Received</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold">Outstanding</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientTransfers.length === 0
                      ? <tr><td colSpan={7} className="text-center py-6 text-gray-400">No transactions</td></tr>
                      : clientTransfers.map(t => {
                          const received = getTransferReceived(t.id);
                          const outstanding = Math.max(0, t.netAmountDue - received);
                          return (
                            <tr key={t.id} className={isDark ? 'border-t border-gray-800' : 'border-t border-gray-100'}>
                              <td className="px-3 py-2">{t.date}</td>
                              <td className="px-3 py-2 font-mono text-xs">{t.transactionId}</td>
                              <td className={`px-3 py-2 font-semibold ${t.type === 'client_paid_me' ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(t.amount)}</td>
                              <td className="px-3 py-2 font-semibold">{formatCurrency(t.netAmountDue)}</td>
                              <td className="px-3 py-2 text-emerald-500 font-semibold">{formatCurrency(received)}</td>
                              <td className="px-3 py-2">
                                {outstanding > 0
                                  ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{formatCurrency(outstanding)}</span>
                                  : received > 0
                                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Done</span>
                                    : <span className="text-xs text-gray-400">—</span>
                                }
                              </td>
                              <td className="px-3 py-2"><StatusBadge status={t.status} /></td>
                            </tr>
                          );
                        })
                    }
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editItem ? 'Edit Client' : 'New Client'}>
        <div className="space-y-3">
          {[{ label: 'Name *', field: 'name', type: 'text' }, { label: 'Phone', field: 'phone', type: 'text' }, { label: 'Email', field: 'email', type: 'email' }].map(f => (
            <div key={f.field}>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{f.label}</label>
              <input type={f.type} value={(form as Record<string, unknown>)[f.field] as string ?? ''} onChange={e => setForm(p => ({ ...p, [f.field]: e.target.value }))} className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-700'}`} />
            </div>
          ))}
          <div className="flex gap-3 justify-end mt-4">
            <button onClick={() => setShowModal(false)} className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
            <button onClick={handleSave} className="px-5 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap">Save</button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => deleteId && deleteClient(deleteId)} title="Delete Client" message="Delete this client? All associated data will remain." />
    </div>
  );
}
